// ComfyUI Action API 模块 - 真实后端接口
import request from '../../util/request.js'

// API 基础配置
const BACKEND_PREFIX = 'https://videofactory.magicpensoft.com'
const API_BASE = `${BACKEND_PREFIX}/api/v1/server/comfyui/action`
const UPLOAD_BASE = `${BACKEND_PREFIX}/api/v1/server/upload`

// 获取认证token
const getAuthToken = () => {
  try {
    return uni.getStorageSync('token') || localStorage.getItem('token') || ''
  } catch (error) {
    return ''
  }
}

// 创建认证头部
const getAuthHeaders = () => {
  const token = getAuthToken()
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// 统一请求处理
const apiRequest = async (url, options = {}) => {
  const config = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers
    },
    ...options
  }

  try {
    const response = await fetch(url, config)
    
    // 检查响应状态
    if (!response.ok) {
      // 如果响应不成功，尝试获取错误信息
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      try {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json()
          errorMessage = errorData.message || errorData.error || errorMessage
        } else {
          // 如果不是JSON响应，获取文本内容
          const errorText = await response.text()
          if (errorText) {
            errorMessage = errorText.substring(0, 200) // 限制错误信息长度
          }
        }
      } catch (parseError) {
        // 如果解析错误信息失败，使用默认错误信息
        console.warn('解析错误响应失败:', parseError)
      }
      
      return {
        success: false,
        error: errorMessage,
        code: response.status
      }
    }
    
    // 检查响应内容类型
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      return {
        success: false,
        error: '服务器返回了非JSON格式的响应',
        code: response.status
      }
    }
    
    // 检查响应是否为空
    const responseText = await response.text()
    if (!responseText.trim()) {
      return {
        success: false,
        error: '服务器返回了空响应',
        code: response.status
      }
    }
    
    // 解析JSON
    let data
    try {
      data = JSON.parse(responseText)
    } catch (jsonError) {
      console.error('JSON解析失败:', jsonError, '响应内容:', responseText.substring(0, 200))
      return {
        success: false,
        error: 'JSON解析失败: ' + jsonError.message,
        code: response.status
      }
    }
    
    // 统一的响应格式处理
    if (data.code === 200) {
      return {
        success: true,
        data: data.data,
        message: data.message,
        code: data.code
      }
    } else {
      return {
        success: false,
        error: data.message || '请求失败',
        code: data.code
      }
    }
  } catch (error) {
    console.error('API请求失败:', error)
    return {
      success: false,
      error: error.message || '网络错误'
    }
  }
}

// API 接口类
class ActionAPI {
  // =============== 1. 用户认证相关接口 ===============
  
  // 1.1 发送短信验证码
  static async sendSmsCode(phone) {
    return await apiRequest(`${API_BASE}/users/send_sms_code/`, {
      method: 'POST',
      body: JSON.stringify({ phone })
    })
  }

  // 1.2 短信验证码登录
  static async loginWithSms(phone, sms_code) {
    return await apiRequest(`${API_BASE}/users/login/`, {
      method: 'POST',
      body: JSON.stringify({ phone, sms_code })
    })
  }

  // 1.3 短信验证码注册
  static async registerWithSms(phone, sms_code, company_name = '', email = '') {
    return await apiRequest(`${API_BASE}/users/register/`, {
      method: 'POST',
      body: JSON.stringify({ phone, sms_code, company_name, email })
    })
  }

  // 1.4 用户退出登录
  static async logout() {
    return await apiRequest(`${API_BASE}/users/logout/`, {
      method: 'POST'
    })
  }

  // =============== 2. 用户信息管理接口 ===============

  // 2.1 获取用户详情
  static async getUserInfo(userId) {
    return await apiRequest(`${API_BASE}/users/${userId}/`)
  }

  // 2.2 更新用户信息
  static async updateUserInfo(userId, userInfo) {
    return await apiRequest(`${API_BASE}/users/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify(userInfo)
    })
  }

  // 2.3 刷新用户剩余次数
  static async refreshRemainingCalls(userId) {
    return await apiRequest(`${API_BASE}/users/refresh-remaining/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }

  // =============== 3. 会员套餐相关接口 ===============

  // 3.1 获取可购买的会员套餐列表
  static async getMemberships(status = 'active') {
    return await apiRequest(`${API_BASE}/memberships/?status=${status}`)
  }

  // 3.2 获取用户会员状态
  static async getUserMemberships(userId, status = 'active') {
    return await apiRequest(`${API_BASE}/user-memberships/?user_id=${userId}&status=${status}`)
  }

  // 3.3 购买会员套餐
  static async purchaseMembership(userId, membershipId) {
    return await apiRequest(`${API_BASE}/user-memberships/`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        membership_id: membershipId
      })
    })
  }

  // =============== 4. 加量包相关接口 ===============

  // 4.1 获取可购买的加量包列表
  static async getPackages(status = 'active') {
    return await apiRequest(`${API_BASE}/packages/?status=${status}`)
  }

  // 4.2 获取用户加量包
  static async getUserPackages(userId, status = 'available') {
    return await apiRequest(`${API_BASE}/user-packages/?user_id=${userId}&status=${status}`)
  }

  // 4.3 购买加量包
  static async purchasePackage(userId, packageId, remainingCalls, expiresAt) {
    return await apiRequest(`${API_BASE}/user-packages/`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        package_id: packageId,
        remaining_calls: remainingCalls,
        used_calls: 0,
        expires_at: expiresAt
      })
    })
  }

  // =============== 5. 模板相关接口 ===============

  // 5.1 获取活动模板列表
  static async getActivityTemplates(activityId = 7, status = 'active', includeParams = true) {
    const params = new URLSearchParams({
      activity_id: activityId,
      status: status,
      include_params: includeParams
    })
    return await apiRequest(`${API_BASE}/activity-templates/?${params}`)
  }

  // =============== 6. 任务执行相关接口 ===============

  // 6.1 执行模板任务
  static async executeTemplate(userId, templateId, activityId, parameters) {
    return await apiRequest(`${API_BASE}/executor/execute-user-template/`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        template_id: templateId,
        activity_id: activityId,
        parameters: parameters
      })
    })
  }

  // 6.2 查询任务状态
  static async getTaskStatus(taskId) {
    return await apiRequest(`${API_BASE}/task-statuses/${taskId}/`)
  }

  // 6.3 获取用户任务历史
  static async getUserTaskHistory(userId, page = 1, pageSize = 20) {
    return await apiRequest(`${API_BASE}/task-statuses/?user_id=${userId}&page=${page}&page_size=${pageSize}`)
  }

  // 6.4 销毁任务
  static async destroyTask(taskId) {
    return await apiRequest(`${API_BASE}/task-status/task/${taskId}/destroy/`, {
      method: 'POST'
    })
  }

  // =============== 7. 文件上传接口 ===============

  // 7.1 上传文件
  static async uploadFile(file, fileName) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', fileName)

    return await apiRequest(UPLOAD_BASE, {
      method: 'POST',
      headers: {
        // 不设置Content-Type，让浏览器自动设置multipart/form-data边界
        ...getAuthHeaders()
      },
      body: formData
    })
  }

  // =============== 8. 活动相关接口 ===============

  // 8.1 获取活跃的活动列表
  static async getActivities(status = 'active') {
    return await apiRequest(`${API_BASE}/activities/?status=${status}`)
  }

  // =============== 便捷方法（兼容旧代码） ===============

  // 兼容旧版本的方法名
  static async getUserQuota(userId) {
    // 获取用户会员状态来计算配额
    const membershipResult = await this.getUserMemberships(userId)
    if (membershipResult.success && membershipResult.data.user_memberships.length > 0) {
      const membership = membershipResult.data.user_memberships[0]
      // 使用新的 today_remaining_calls 字段（支持累加），如果没有则回退到旧逻辑
      const todayRemainingCalls = membership.today_remaining_calls !== undefined 
        ? membership.today_remaining_calls 
        : (membership.remaining_calls !== undefined 
          ? membership.remaining_calls 
          : Math.max(0, (membership.daily_limit || 10) - (membership.today_calls || 0)))
      
      return {
        success: true,
        data: {
          todayCalls: membership.today_calls || 0,
          dailyLimit: membership.daily_limit || 10,
          totalCalls: membership.today_calls || 0,
          remainingCalls: todayRemainingCalls,
          todayRemainingCalls: todayRemainingCalls,
          remainingCallsLastRefresh: membership.remaining_calls_last_refresh || null
        }
      }
    } else {
      // 返回默认配额
      return {
        success: true,
        data: {
          todayCalls: 0,
          dailyLimit: 10,
          totalCalls: 0,
          remainingCalls: 10,
          todayRemainingCalls: 10,
          remainingCallsLastRefresh: null
        }
      }
    }
  }

  // 获取模板列表（兼容旧代码）
  static async getTemplates() {
    const result = await this.getActivityTemplates()
    if (result.success) {
      // 转换数据格式以兼容旧代码
      const templates = result.data.activity_templates.map(template => ({
        key: template.template_id,
        id: template.id,
        name: template.template_name,
        description: template.description,
        image: template.thumbnail || template.cover,
        thumbnail: template.thumbnail,
        cover: template.cover,
        category: template.category,
        tags: template.tags,
        parameters: template.parameters,
        estimatedDuration: template.estimated_duration
      }))
      
      return {
        success: true,
        data: templates
      }
    }
    return result
  }

  // 生成视频（兼容旧代码）
  static async generateVideo(params) {
    const { userId, templateId, parameters } = params
    const result = await this.executeTemplate(userId, templateId, '7', parameters)
    
    if (result.success) {
      return {
        success: true,
        data: {
          taskId: result.data.task_id,
          dbTaskId: result.data.db_task_id,
          status: result.data.status,
          queryUrl: result.data.query_url
        }
      }
    }
    return result
  }
}

// 用户相关API（兼容旧代码）
export const ActionUserAPI = {
  // 发送短信验证码
  async sendSmsCode(params) {
    // 处理传入的参数，支持字符串和对象两种格式
    const phone = typeof params === 'string' ? params : params.phone
    return await ActionAPI.sendSmsCode(phone)
  },

  // 手机号登录
  async loginWithPhone(params) {
    const { phone, sms_code } = params
    return await ActionAPI.loginWithSms(phone, sms_code)
  },

  // 短信验证码登录（别名方法，为了兼容性）
  async loginWithSms(params) {
    return await this.loginWithPhone(params)
  },

  // 手机号注册
  async registerWithPhone(params) {
    const { phone, sms_code, company_name, email } = params
    return await ActionAPI.registerWithSms(phone, sms_code, company_name, email)
  }
}

export default ActionAPI